<template>
  <div style="min-height: 100%; margin-top: -4px">
    <RecycleBinToolsBar
      v-if="!refreshInProgress"
      :view-type="getNodeViewType"
      :showing-confirmation-dialog="isShowingConfirmationDialog"
      :deletion-in-progress="deletionInProgress"
      :show-previews="showPreviews"
      :deletion-type-filter="deletionTypeFilter"
      @restoreAll="restoreAll"
      @restoreSelected="restoreSelectedItems"
      @permanentlyDeleteAll="permanentlyDeleteAll"
      @permanentlyDeleteSelected="permanentlyDeleteSelectedItems"
      @searchByFileName="searchByFileName"
      @toggleView="toggleViewType"
      @confirmationDialogCancel="hideRecycleBinConfirmationDialog"
      @confirmationDialogShow="showRecycleBinConfirmationDialog"
      @thumbnailSettingChanged="handleThumbnailSettingChanged"
      @filterChanged="handleFilterChanged"
    />

    <RecycleBinArchivedDialog v-model="showingArchivedDialog" />

    <div v-if="refreshInProgress">
      <v-col class="text-center">
        <v-progress-circular
          indeterminate
          size="70"
          width="7"
          color="primary"
          :aria-label="$t('routes.node.loadingFolder')"
        />
        <div>{{ $t('routes.node.loadingFolder') }}</div>
      </v-col>
    </div>

    <v-row v-if="!refreshInProgress && !this.$canListNodeAndPod()">
      <v-col>
        <v-alert
          type="error"
          class="pa-4"
          prepend-icon="mdi-alert"
        >
          <div class="text-left">
            <h4>{{ $t('routes.node.accessDenied') }}</h4>
          </div>
          <span>{{ $t('routes.node.haveNoPermissions') }}</span>
        </v-alert>
      </v-col>
    </v-row>

    <v-container v-if="!refreshInProgress && this.$canListNodeAndPod()">
      <RecycleBinContextMenu
        v-if="this.$canControlNodeAndPod() || this.$canDeleteNodeAndPod()"
        v-model="isShowingMenu"
        :position-x="contextMenuX"
        :position-y="contextMenuY"
        :item-type="contextMenuItemType"
        :deletion-in-progress="deletionInProgress"
        :confirmation-dialog-error-message="confirmationDialogErrorMessage"
        :is-showing-confirmation-dialog="isShowingConfirmationDialog"
        :is-archived="contextMenuItem && contextMenuItem.archived > 0"
        @restoreItem="restoreItem(contextMenuItemType, contextMenuItem)"
        @permanentlyDeleteItem="permanentlyDeleteItem(contextMenuItemType, contextMenuItem)"
        @confirmationDialogShow="showRecycleBinConfirmationDialog"
      />

      <div v-for="nodeItem in getPagedNodes" :key="nodeItem.id" class="mt-4" role="region" :aria-label="getBreadcrumbPath(nodeItem)">
        <v-row>
          <v-col>
            <div class="d-flex align-center">
              <!-- Fold/Unfold Toggle Button -->
              <v-btn
                :icon="isNodeFolded(nodeItem.id) ? 'mdi-chevron-right' : 'mdi-chevron-down'"
                size="x-small"
                @click="toggleNodeFold(nodeItem.id)"
                class="mr-2"
                color="secondary"
                variant="outlined"
                style="border-radius: 10px;"
                :aria-label="isNodeFolded(nodeItem.id) ? $t('common.expand') : $t('common.collapse')"
              />

              <!-- Node Path with Tooltip -->
              <v-tooltip location="bottom" :aria-label="getBreadcrumbPath(nodeItem)">
                <template v-slot:activator="{ props }">
                  <div v-bind="props">
                    <span
                      v-if="getActiveAccesspoint &&
                        getNodeById(nodeItem.parentid) &&
                        getNodeById(nodeItem.parentid).id !== getActiveAccesspoint.node.id"
                    >
                      <component
                        :is="!getActiveAccesspoint.node.deletedat ? 'router-link' : 'span'"
                        :to="!getActiveAccesspoint.node.deletedat ? `/node/${getActiveAccesspoint.node.id}` : undefined"
                      >
                        <v-icon>{{ getNodeIcon(getActiveAccesspoint.node) }}</v-icon>
                        {{ getActiveAccesspoint.node.name }}
                      </component>

                      <v-icon>mdi-chevron-right</v-icon>
                    </span>

                    <span
                      v-if="getActiveAccesspoint &&
                        getNodeById(nodeItem.parentid) &&
                        getNodeById(nodeItem.parentid).id !== getActiveAccesspoint.node.id &&
                        getNodeById(nodeItem.parentid).parentid !== getActiveAccesspoint.node.id"
                    >
                      ...
                      <v-icon>mdi-chevron-right</v-icon>
                    </span>

                    <span v-if="getNodeById(nodeItem.parentid)">
                      <component
                        :is="!getNodeById(nodeItem.parentid).deletedat ? 'router-link' : 'span'"
                        :to="!getNodeById(nodeItem.parentid).deletedat ? `/node/${getNodeById(nodeItem.parentid).id}` : undefined"
                      >
                        <v-icon>{{ getNodeIcon(getNodeById(nodeItem.parentid)) }}</v-icon>
                        {{ getNodeById(nodeItem.parentid).name }}
                      </component>

                      <v-icon>mdi-chevron-right</v-icon>
                    </span>

                    <span>
                      <component
                        :is="!nodeItem.deletedat ? 'router-link' : 'span'"
                        :to="!nodeItem.deletedat ? `/node/${nodeItem.id}` : undefined"
                      >
                        <v-icon>{{ getNodeIcon(nodeItem) }}</v-icon>
                        {{ nodeItem.name }}
                      </component>

                      <v-chip
                        size="x-small"
                        class="ml-2"
                      >
                        {{ getDeletedPodsByNodeid(nodeItem.id).length }}
                      </v-chip>
                    </span>
                  </div>
                </template>
                <span>{{ getBreadcrumbPath(nodeItem) }}</span>
              </v-tooltip>
            </div>
          </v-col>
        </v-row>

        <!-- Collapsible Content -->
        <v-expand-transition>
          <v-row v-show="!isNodeFolded(nodeItem.id)">
            <RecycleBinGridView
              v-if="getNodeViewType === 'grid'"
              :items="getDeletedPodsByNodeid(nodeItem.id)"
              :selected-items-count="selectedItemsCount"
              :is-showing-confirmation-dialog="isShowingConfirmationDialog"
              :deletion-in-progress="deletionInProgress"
              :confirmation-dialog-error-message="confirmationDialogErrorMessage"
              :show-previews="showPreviews"
              @restoreItem="restoreItem"
              @permanentlyDeleteItem="permanentlyDeleteItem"
              @confirmationDialogShow="showRecycleBinConfirmationDialog"
              @showContextMenu="showContextMenu"
            />

            <RecycleBinListView
              v-if="getNodeViewType === 'list'"
              :items="getDeletedPodsByNodeid(nodeItem.id)"
              :selected-items-count="selectedItemsCount"
              :is-showing-confirmation-dialog="isShowingConfirmationDialog"
              :deletion-in-progress="deletionInProgress"
              :confirmation-dialog-error-message="confirmationDialogErrorMessage"
              :show-previews="showPreviews"
              @restoreItem="restoreItem"
              @permanentlyDeleteItem="permanentlyDeleteItem"
              @confirmationDialogShow="showRecycleBinConfirmationDialog"
              @showContextMenu="showContextMenu"
            />
          </v-row>
        </v-expand-transition>
      </div>

      <v-row v-if="!updateInProgress">
        <v-col class="d-flex justify-center">
          <div style="width: fit-content;">
            {{ $t('routes.node.showingNumberOfFolders', [getPagedNodes.length, getNodesWithPods.length]) }}

            <v-progress-linear
              v-if="getNodesWithPods.length !== 0"
              v-show="getPagedNodes.length !== getNodesWithPods.length && isLoadingMore"
              height="4"
              indeterminate
            />

          </div>
        </v-col>
      </v-row>

      <!-- Sentinel element for scroll detection -->
      <div ref="scrollSentinel" style="height: 1px;"></div>
    </v-container>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useRecycleBinStore } from '@/stores/RecycleBinStore.js';
import _debounce from 'lodash/debounce';
import RecycleBinToolsBar from '@/routes/recycle-bin/components/RecycleBinToolsBar.vue';
import RecycleBinGridView from '@/routes/recycle-bin/components/RecycleBinGridView.vue';
import RecycleBinListView from '@/routes/recycle-bin/components/RecycleBinListView.vue';
import RecycleBinContextMenu from '@/routes/recycle-bin/components/RecycleBinContextMenu.vue';
import RecycleBinArchivedDialog from '@/routes/recycle-bin/components/RecycleBinArchivedDialog.vue';

export default {
  name: 'RecycleBinIndex',
  components: {
    RecycleBinToolsBar,
    RecycleBinGridView,
    RecycleBinListView,
    RecycleBinContextMenu,
    RecycleBinArchivedDialog,
  },
  data() {
    return {
      isShowingConfirmationDialog: false,
      showingArchivedDialog: false,
      deletionInProgress: false,
      confirmationDialogErrorMessage: '',

      searchText: '',
      deletionTypeFilter: 'all',

      isShowingMenu: false,
      contextMenuX: 0,
      contextMenuY: 0,
      contextMenuItemType: 'pod',
      contextMenuItem: null,

      updateInProgress: false,
      isLoadingMore: false,
      itemsPerPage: 2,
      showingItems: 0,

      // Node folding state - stores nodeId -> boolean (true = folded)
      foldedNodes: {},

      // Show previews setting - get from localStorage or default to true
      showPreviews: this.getShowPreviewsFromStorage(),
    };
  },
  async mounted() {
    const recycleBinStore = useRecycleBinStore();

    if (this.getRootNodeId) {
      const payload = {
        rootNodeId: this.getRootNodeId,
        silent: false,
      };

      await recycleBinStore.refreshNodesList(payload);
    }

    recycleBinStore.deselectAllPods();
    this.setActiveNodeIfNeeded();

    // Setup scroll listener after Vue has rendered
    this.$nextTick(() => {
      this.setupScrollListener();
      this.setupIntersectionObserver();

      // Initialize all nodes as folded by default
      this.initializeFoldedState();
    });
  },
  beforeUnmount() {
    this.cleanupScrollListener();
    this.cleanupIntersectionObserver();
    this.resetShowingItems();
  },
  watch: {
    getAccessPoints() {
      this.setActiveNodeIfNeeded();
    },
    getNodeViewType() {
      this.resetShowingItems();
    },
    searchText() {
      this.resetShowingItems();
    },
    deletionTypeFilter() {
      this.resetShowingItems();
    },
    getNodesWithPods: {
      handler() {
        this.$nextTick(() => {
          this.setupIntersectionObserver();
        });
      },
    },
  },
  methods: {
    setupScrollListener() {
      // Find the v-main element which is the scroll container
      this.scrollContainer = document.querySelector('.v-main__wrap');

      if (!this.scrollContainer) {
        // Fallback to window if v-main not found
        this.scrollContainer = window;
      }

      this.scrollHandler = _debounce(() => {
        const element = this.scrollContainer === window ? document.documentElement : this.scrollContainer;
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;

        // Trigger when user is within 200px of the bottom
        if (scrollTop + clientHeight >= scrollHeight - 200) {
          this.loadMoreItems();
        }
      }, 200);

      this.scrollContainer.addEventListener('scroll', this.scrollHandler, { passive: true });
    },

    cleanupScrollListener() {
      if (this.scrollContainer && this.scrollHandler) {
        this.scrollContainer.removeEventListener('scroll', this.scrollHandler);
      }
    },

    setupIntersectionObserver() {
      // Clean up existing observer
      this.cleanupIntersectionObserver();

      // Create intersection observer for the sentinel element
      if (this.$refs.scrollSentinel && window.IntersectionObserver) {
        // Use the scroll container as root if it's not window
        const root = this.scrollContainer === window ? null : this.scrollContainer;

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.isLoadingMore && !this.updateInProgress) {
                const hasMoreItems = this.getPagedNodes.length < this.getNodesWithPods.length;
                if (hasMoreItems) {
                  this.loadMoreItems();
                }
              }
            });
          },
          {
            root,
            rootMargin: '200px',
            threshold: 0.1,
          }
        );

        this.intersectionObserver.observe(this.$refs.scrollSentinel);
      }
    },

    cleanupIntersectionObserver() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
    },

    // Storage methods for showPreviews
    getShowPreviewsFromStorage() {
      try {
        const stored = localStorage.getItem('recycleBinShowPreviews');
        return stored !== null ? JSON.parse(stored) : true;
      } catch (error) {
        console.warn('Error reading showPreviews from localStorage:', error);
        return true;
      }
    },

    saveShowPreviewsToStorage(value) {
      try {
        localStorage.setItem('recycleBinShowPreviews', JSON.stringify(value));
      } catch (error) {
        console.warn('Error saving showPreviews to localStorage:', error);
      }
    },

    handleThumbnailSettingChanged(showThumbnails) {
      this.showPreviews = showThumbnails;
      this.saveShowPreviewsToStorage(showThumbnails);
    },

    handleFilterChanged(filterType) {
      this.deletionTypeFilter = filterType;
    },

    // Check if a pod was deleted due to retention policy
    isPodDeletedByRetention(pod) {
      return pod?.jsondata?.retention !== undefined;
    },

    // Node folding methods
    initializeFoldedState() {
      // Set all nodes to folded by default
      this.getNodesWithPods.forEach((node) => {
        if (!(node.id in this.foldedNodes)) {
          this.foldedNodes[node.id] = true;
        }
      });
    },

    toggleNodeFold(nodeId) {
      const currentState = this.foldedNodes[nodeId] || false;
      this.foldedNodes[nodeId] = !currentState;
    },

    isNodeFolded(nodeId) {
      return this.foldedNodes[nodeId] || false;
    },

    setActiveNodeIfNeeded() {
      // When we refresh any page on the layout we are losing the active node.
      // So we set the access point as active node.
      if (!this.getCurrentNode &&
        this.getRootNodeId &&
        this.getAccessPoints &&
        this.getAccessPoints.length > 0) {
        const nodeStore = useNodeStore();
        nodeStore.setActiveNode(this.getRootNodeId);
      }
    },

    resetShowingItems() {
      this.showingItems = this.itemsPerPage;
      this.isLoadingMore = false;
      this.$nextTick(() => {
        this.setupIntersectionObserver();
      });
    },

    loadMoreItems() {
      if (this.isLoadingMore || this.showingItems >= this.getNodesWithPods.length) {
        return;
      }

      this.isLoadingMore = true;

      // Simulate async loading
      setTimeout(() => {
        this.showingItems = Math.min(
          this.showingItems + this.itemsPerPage,
          this.getNodesWithPods.length
        );
        this.isLoadingMore = false;

        // Re-setup observer after loading more items
        this.$nextTick(() => {
          this.setupIntersectionObserver();
        });
      }, 300);
    },

    async restoreItem(type, item) {
      if (item.archived) {
        this.showingArchivedDialog = true;
        return;
      }

      let podIds = [];

      if (type === 'pod') {
        podIds = [item.id];
      }

      const recycleBinStore = useRecycleBinStore();

      podIds = this.getPodidsWithVersions(podIds);
      if (podIds.length === 0) {
        this.$showMessage(this.$t('routes.recycleBin.noItemsToRestore', 'No items to restore'), { type: "error" });
        return;
      }

      if (recycleBinStore.isRestoringPodIds(podIds)) {
        this.$showMessage(this.$t('routes.recycleBin.filesAreBeingRestored'), { type: 'error' });
        return;
      }

      await recycleBinStore.restorePods(podIds);
    },
    async restoreSelectedItems() {
      if (this.getSelectedPods.length === 0) {
        this.$showMessage(this.$t('routes.recycleBin.noItemsSelected', 'No items selected'), { type: "error" });
        return;
      }

      if (this.getSelectedPods.some((pod) => pod.archived > 0)) {
        this.showingArchivedDialog = true;
        return;
      }

      let podIds = this.getSelectedPods.map((pod) => pod.id);
      podIds = this.getPodidsWithVersions(podIds);

      if (podIds.length > 0) {
        const recycleBinStore = useRecycleBinStore();

        if (recycleBinStore.isRestoringPodIds(podIds)) {
          this.$showMessage(this.$t('routes.recycleBin.filesAreBeingRestored'), { type: 'error' });
          return;
        }

        await recycleBinStore.restorePods(podIds);
      }
    },
    async restoreAll() {
      if (this.getAllPods.length === 0) {
        this.$showMessage(this.$t('routes.recycleBin.noItemsToRestore', 'No items to restore'), { type: "error" });
        return;
      }

      if (this.getAllPods.some((pod) => pod.archived)) {
        this.showingArchivedDialog = true;
        return;
      }

      let podIds = this.getAllPods.map((pod) => pod.id);

      podIds = this.getPodidsWithVersions(podIds);

      const recycleBinStore = useRecycleBinStore();

      if (recycleBinStore.isRestoringPodIds(podIds)) {
        this.$showMessage(this.$t('routes.recycleBin.filesAreBeingRestored'), { type: 'error' });
        return;
      }

      await recycleBinStore.restorePods(podIds);

      const node = this.getNodeById(this.currentNodeId);
      if (node.parentid) {
        await this.$router.replace({ path: `/recycle-bin/${node.parentid}` });
      } else {
        const authStore = useAuthStore();
        const rootNodeId = authStore.rootNodeId;
        await this.$router.replace({ path: `/node/${rootNodeId}` });
      }
    },
    async permanentlyDeleteAll() {
      if (this.getAllPods.length === 0) {
        this.$showMessage(this.$t('routes.recycleBin.noItemsToDelete', 'No items to delete'), { type: "error" });
        return;
      }

      this.deletionInProgress = true;

      try {
        const recycleBinStore = useRecycleBinStore();

        // If a filter is active, delete only the filtered pods
        if (this.deletionTypeFilter !== 'all') {
          let podIds = this.getAllPods.map((pod) => pod.id);
          podIds = this.getPodidsWithVersions(podIds);
          await recycleBinStore.permanentlyDeletePods(podIds);
        } else {
          // If no filter is active, empty the entire recycle bin
          await recycleBinStore.emptyRecycleBin();
        }

        this.isShowingConfirmationDialog = false;
      } catch (error) {
        this.confirmationDialogErrorMessage = error.message;
      } finally {
        this.deletionInProgress = false;
      }
    },
    async permanentlyDeleteSelectedItems() {
      if (this.getSelectedPods.length === 0) {
        this.$showMessage(this.$t('routes.recycleBin.noItemsSelected', 'No items selected'), { type: "error" });
        return;
      }

      let podIds = this.getSelectedPods.map((pod) => pod.id);

      if (podIds.length > 0) {
        this.deletionInProgress = true;

        podIds = this.getPodidsWithVersions(podIds);

        try {
          const recycleBinStore = useRecycleBinStore();
          await recycleBinStore.permanentlyDeletePods(podIds);
          this.isShowingConfirmationDialog = false;
        } catch (error) {
          this.confirmationDialogErrorMessage = error.message;
        } finally {
          this.deletionInProgress = false;
        }
      }
    },
    async permanentlyDeleteItem(type, item) {
      let podIds = [];

      if (type === 'pod') {
        podIds = [item.id];
      }

      podIds = this.getPodidsWithVersions(podIds);

      if (podIds.length === 0) {
        this.$showMessage(this.$t('routes.recycleBin.noItemsToDelete', 'No items to delete'), { type: "error" });
        return;
      }

      this.deletionInProgress = true;

      try {
        const recycleBinStore = useRecycleBinStore();
        await recycleBinStore.permanentlyDeletePods(podIds);
        this.isShowingConfirmationDialog = false;
      } catch (error) {
        this.confirmationDialogErrorMessage = error.message;
      } finally {
        this.deletionInProgress = false;
      }
    },
    searchByFileName(text) {
      this.searchText = text;
    },
    toggleViewType() {
      window.scrollTo(0, 0);

      // store the value of folder view after toggle.
      const nodeStore = useNodeStore();
      nodeStore.toggleNodeView();
    },
    showRecycleBinConfirmationDialog() {
      this.isShowingConfirmationDialog = true;
      this.deletionInProgress = false;
      this.confirmationDialogErrorMessage = '';
    },
    hideRecycleBinConfirmationDialog() {
      this.isShowingConfirmationDialog = false;
      this.deletionInProgress = false;
      this.confirmationDialogErrorMessage = '';
    },
    getPodidsWithVersions(podIds) {
      let versionsIds = [];

      for (let i = 0; i < podIds.length; i++) {
        const recycleBinStore = useRecycleBinStore();
        const deletedPodVersions = recycleBinStore.getDeletedPodVersions(podIds[i]);

        const podVersionIds = deletedPodVersions.map((pod) => pod.id);
        versionsIds = versionsIds.concat(podVersionIds);
      }

      return versionsIds;
    },
    showContextMenu(event) {
      if (this.$canControlNodeAndPod() || this.$canDeleteNodeAndPod()) {
        this.isShowingMenu = false;
        this.contextMenuX = event.contextMenuX;
        this.contextMenuY = event.contextMenuY;

        if ('contextMenuNode' in event) {
          this.contextMenuItemType = 'node';
          this.contextMenuItem = event.contextMenuNode;
        } else {
          this.contextMenuItemType = 'pod';
          this.contextMenuItem = event.contextMenuPod;
        }

        this.$nextTick(() => {
          this.isShowingMenu = true;
        });
      }
    },
    getBreadcrumbPath(nodeItem) {
      if (!nodeItem.breadcrumb || nodeItem.breadcrumb.length === 0) {
        return nodeItem.name;
      }

      return nodeItem.breadcrumb;
    },
  },
  computed: {
    getNodeById() {
      return (id) => {
        const recycleBinStore = useRecycleBinStore();
        return recycleBinStore.getNodeById(id);
      };
    },
    getNodesWithPods() {
      const recycleBinStore = useRecycleBinStore();
      return recycleBinStore.getAllNodes.filter((node) => this.getDeletedPodsByNodeid(node.id).length > 0);
    },
    getAllPods() {
      const recycleBinStore = useRecycleBinStore();
      // Apply filter to all pods
      const allPods = recycleBinStore.getAllPods;

      if (this.deletionTypeFilter === 'retention') {
        return allPods.filter((pod) => this.isPodDeletedByRetention(pod));
      }

      return allPods;
    },
    getDeletedPodsByNodeid() {
      return (nodeid) => {
        const theNode = this.getNodeById(nodeid);
        const nodeNamesLowercase = theNode.breadcrumbNodes.map(x => x.name.toLowerCase())

        return theNode.pods
          .filter((pod) => pod.deletedat !== null)
          .filter((pod) => pod.parentpodid === null)
          .filter((pod) => {
            // Apply deletion type filter
            if (this.deletionTypeFilter === 'retention') {
              if (!this.isPodDeletedByRetention(pod)) {
                return false;
              }
            }

            // Apply search filter
            if (this.searchText) {
              // filter by node name
              if (nodeNamesLowercase.some(name => name.includes(this.searchText.toLowerCase()))) {
                return true;
              }

              // filter by pod name
              return pod.name.toLowerCase().includes(this.searchText.toLowerCase());
            }

            return true;
          });
      };
    },
    getPagedNodes() {
      return this.getNodesWithPods.slice(0, this.showingItems);
    },

    getNodeIcon() {
      return (node) => {
        if (node.archived === 1 || node.archived === 2) {
          return 'mdi-package-down';
        }

        switch (node.type) {
          case 1: return 'mdi-folder';
          case 2: return 'mdi-briefcase-clock';
          case 3: return 'tw tw-office-building';
          case 4: return 'mdi-home-assistant';
          default: return 'mdi-folder';
        }
      };
    },

    getNodeViewType() {
      const nodeStore = useNodeStore();
      return nodeStore.getNodeView;
    },
    refreshInProgress() {
      const recycleBinStore = useRecycleBinStore();
      return recycleBinStore.getIsRefreshingNodes;
    },
    getActiveAccesspoint() {
      const authStore = useAuthStore();
      return authStore.getActiveAccesspoint;
    },

    recycleBinItems() {
      const currentNode = this.getNodeById(this.$route.params.id);

      if (!currentNode) return [];

      const recycleBinStore = useRecycleBinStore();

      const childNodes = recycleBinStore.getChildNodes(this.currentNodeId);
      return [...childNodes, ...currentNode.pods];
    },
    getSelectedPods() {
      const recycleBinStore = useRecycleBinStore();
      const selectedPods = recycleBinStore.getSelectedPods;

      // Apply filter to selected pods as well
      if (this.deletionTypeFilter === 'retention') {
        return selectedPods.filter((pod) => this.isPodDeletedByRetention(pod));
      }

      return selectedPods;
    },
    selectedItemsCount() {
      return this.getSelectedPods.length;
    },
    currentNodeId() {
      return this.$route.params.id;
    },

    getAccessPoints() {
      const nodeStore = useNodeStore();
      return nodeStore.getAccessPoints;
    },
    getRootNodeId() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    getCurrentNode() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
  },
};
</script>

<style scoped>

</style>
